import { useEffect, useRef } from 'react';
import { Ghost } from 'lucide-react';

/**
 * Renders peer cursors as colored dots overlaid at viewport-relative
 * coordinates, and (when `perform` is provided) broadcasts the local
 * mouse position throttled at ~50ms.
 *
 * Coordinates flow as percentages of viewport width/height — this means
 * cursors land at the same DOM-content position only when both sides
 * have similar viewport dimensions, but it requires no shared anchor
 * element and works regardless of scroll position.
 *
 * If a peer is `away` (their tab is hidden / window unfocused), their
 * cursor renders as a gray ghost icon at its last-known position
 * instead of the colored arrow.
 */
export default function LiveCursorOverlay({
  cursors = {},
  focuses = {},
  aways = {},
  perform,
  selfUserId,
}) {
  const lastSentRef = useRef(0);

  useEffect(() => {
    if (!perform) return;
    const handleMove = (e) => {
      const now = Date.now();
      if (now - lastSentRef.current < 50) return;
      lastSentRef.current = now;
      const x = (e.clientX / window.innerWidth) * 100;
      const y = (e.clientY / window.innerHeight) * 100;
      perform('cursor_move', { x, y });
    };
    document.addEventListener('mousemove', handleMove);
    return () => document.removeEventListener('mousemove', handleMove);
  }, [perform]);

  // Determine "away" for a peer — only students can be ghosted; teachers
  // are never rendered as ghosts even if an away_state with role=teacher
  // ever sneaks through (the broadcaster suppresses sending in the first
  // place; this is the defensive second guard).
  const isPeerAway = (userId, cursor) => {
    if (cursor?.role === 'teacher') return false;
    return !!aways[userId]?.away;
  };

  // Suppress a peer's free-floating cursor dot when they're focused on a
  // tagged element — the focus outline (rendered by RemoteHoverFocus) is
  // the more semantically accurate indicator of where they're pointing.
  // Away peers still render (as a gray ghost) so we can see "they're
  // here, just not looking at the page right now".
  const peers = Object.entries(cursors).filter(([userId, c]) => {
    if (String(userId) === String(selfUserId)) return false;
    if (isPeerAway(userId, c)) return true;
    const focus = focuses[userId];
    if (focus && focus.key) return false;
    return true;
  });
  if (peers.length === 0) return null;

  return (
    <div className="pointer-events-none fixed inset-0 z-40">
      {peers.map(([userId, c]) => (
        <Cursor
          key={userId}
          role={c.role}
          x={c.x}
          y={c.y}
          name={c.name}
          away={isPeerAway(userId, c)}
        />
      ))}
    </div>
  );
}

function Cursor({ role, x, y, name, away }) {
  const liveColor =
    role === 'teacher' ? '#3b82f6' /* blue-500 */ : '#10b981'; /* emerald-500 */
  const color = away ? '#9ca3af' /* gray-400 */ : liveColor;
  const label = name || (role === 'teacher' ? 'Teacher' : 'Student');

  return (
    <div
      className="absolute"
      style={{
        left: `${Math.max(0, Math.min(100, x))}%`,
        top: `${Math.max(0, Math.min(100, y))}%`,
        transform: 'translate(-2px, -2px)',
        transition: 'left 80ms linear, top 80ms linear',
      }}
    >
      {away ? (
        <Ghost
          width={22}
          height={22}
          color={color}
          strokeWidth={1.75}
          fill="rgba(156, 163, 175, 0.25)"
          style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.25))' }}
        />
      ) : (
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))' }}
        >
          <path
            d="M2 2 L2 16 L6 12 L9 18 L11.5 17 L8.5 11 L14 11 Z"
            fill={color}
            stroke="white"
            strokeWidth="1"
            strokeLinejoin="round"
          />
        </svg>
      )}
      {label && (
        <span
          className="ml-1 px-1.5 py-0.5 text-[10px] font-semibold text-white rounded"
          style={{ backgroundColor: color, opacity: away ? 0.85 : 1 }}
        >
          {label}
          {away && <span className="ml-1 opacity-80">· away</span>}
        </span>
      )}
    </div>
  );
}
